import React, { useState } from 'react';
import { useData } from "../backend/firebase";
import { DateTime } from 'luxon';
import MultiStreamPlayer from '../components/MultiStreamPlayer';
import { getImagePath } from '../utils/imageRegistry';
import SEO from '../components/SEO';

const Archive = () => {
  const { schedule, teams } = useData();
  const [selectedEvent, setSelectedEvent] = useState(null);
  const now = DateTime.now();

  // Filter for past events
  const pastEvents = schedule.filter(e => {
      const end = DateTime.fromISO(e.endTime);
      return end < now;
  }).sort((a, b) => new Date(b.startTime) - new Date(a.startTime)); 

  const renderSvgPreview = (svgString) => {
      if(!svgString) return null;
      const dataUrl = `data:image/svg+xml;utf8,${encodeURIComponent(svgString)}`;
      return <img src={dataUrl} alt="logo" style={{width: '100%', height: '100%', objectFit: 'contain'}} />;
  };

  const TeamIcon = ({ teamId, size = 32 }) => {
      const team = teams.find(t => t.id.toString() === teamId?.toString());
      const style = {
          width: size, height: size, borderRadius: 8, 
          background: team?.color || '#333', 
          boxShadow: `0 4px 10px rgba(0,0,0,0.3)`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          overflow: 'hidden' // Keeps square images rounded
      };

      if (!team) return <div style={style}></div>;

      // 1. SVG (Legacy)
      if (team.logo) {
          return (
              <div style={{...style, background: 'rgba(255,255,255,0.1)', padding: 4}}>
                  {renderSvgPreview(team.logo)}
              </div>
          );
      }
      
      // 2. Standard Image (New)
      if (team.logoUrl) {
          return (
              <div style={{...style, background: 'rgba(255,255,255,0.1)', padding: 2}}>
                  <img 
                      src={getImagePath(team.logoUrl)} 
                      alt={team.name} 
                      style={{width: '100%', height: '100%', objectFit: 'contain'}} 
                  />
              </div>
          );
      }

      // 3. Fallback Color
      return <div style={style}></div>;
  };

  return (
    <div className="PageLayout">
        <SEO 
            title="Past Streams & VODs" 
            description="Missed a match? Watch past broadcasts, highlights, and VODs from the Bridge Brawl tournament."
            url="/archive" 
        />
        <div className="ContentWrapper">
            
            {/* --- PLAYER SECTION (Visible if event selected) --- */}
            {selectedEvent && (
                <div className="GlassCard" style={{padding: 0, marginBottom: 30, height: 500, overflow: 'hidden', flex: 'none'}}>
                    <div style={{background: '#151823', padding: '10px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #333'}}>
                        <span style={{fontWeight: 'bold'}}>Now Playing: {selectedEvent.title}</span>
                        <button onClick={() => setSelectedEvent(null)} style={{background: 'transparent', border: 'none', color: '#FF5C5C', cursor: 'pointer'}}>Close Player</button>
                    </div>
                    <MultiStreamPlayer event={selectedEvent} />
                </div>
            )}

            <div className="GlassCard">
                <div className="ScrollArea">
                    <h1 className="SectionTitle">Past Streams & Results</h1>
                    {pastEvents.length === 0 && <div style={{opacity: 0.5}}>No archived events found.</div>}
                    
                    {pastEvents.map(event => {
                        const start = DateTime.fromISO(event.startTime);
                        const hasLink = (event.links && event.links.length > 0) || event.link;
                        const isPlaying = selectedEvent && selectedEvent.id === event.id;

                        return (
                            <div 
                                key={event.id} 
                                onClick={() => {
                                    if(hasLink) {
                                        setSelectedEvent(event);
                                        window.scrollTo({top:0, behavior:'smooth'});
                                    }
                                }}
                                style={{
                                    display: 'flex', alignItems: 'center', padding: '15px', marginBottom: 10, borderRadius: 12,
                                    background: isPlaying ? 'rgba(108, 93, 211, 0.1)' : 'rgba(255,255,255,0.02)', 
                                    border: isPlaying ? '1px solid #6C5DD3' : '1px solid rgba(255,255,255,0.05)',
                                    cursor: hasLink ? 'pointer' : 'default'
                                }}
                            >
                                <div style={{width: 100, textAlign: 'center', marginRight: 15}}>
                                    <div style={{fontWeight: 'bold', fontSize: 14, color: '#8F9EB5'}}>{start.toFormat('LLL dd')}</div>
                                    <div style={{fontSize: 12, color: '#555'}}>{start.toFormat('yyyy')}</div>
                                </div>
                                
                                <div style={{flex: 1}}>
                                    <div style={{display: 'flex', alignItems: 'center', marginBottom: 5}}>
                                        <div style={{display: 'flex', alignItems: 'center', marginRight: 10, gap: 5}}>
                                            <TeamIcon teamId={event.team1} />
                                            <span style={{fontSize: 10, color:'#888'}}>vs</span>
                                            <TeamIcon teamId={event.team2} />
                                        </div>
                                        <div style={{fontSize: 16, fontWeight: 'bold', color: 'white'}}>
                                            {event.title}
                                            {event.result && <span style={{marginLeft: 10, color: '#FFD700', fontSize: 14, background: 'rgba(255, 215, 0, 0.1)', padding: '2px 6px', borderRadius: 4}}>{event.result}</span>}
                                        </div>
                                    </div>
                                    {event.description && <div style={{fontSize: 13, color: '#8F9EB5'}}>{event.description}</div>}
                                </div>

                                {hasLink ? (
                                    <button style={{
                                        background: '#2D2B3F', color: 'white', border: 'none',
                                        padding: '8px 16px', borderRadius: 8, cursor: 'pointer', fontWeight: 'bold', fontSize: 13
                                    }}>
                                        {isPlaying ? 'Playing...' : 'Watch VOD'}
                                    </button>
                                ) : (
                                    <span style={{fontSize: 12, color: '#555', fontStyle: 'italic'}}>No VOD</span>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    </div>
  );
};

export default Archive;